import { DetailedHTMLProps, FC, HTMLAttributes, useContext } from "react";
import { AuthContext, AuthContextType } from "../context/AuthProvider";
import { useQuery } from "@tanstack/react-query";
import { ParsedToken } from "firebase/auth";

declare global {
	namespace JSX {
		interface IntrinsicElements {
			"stripe-pricing-table": DetailedHTMLProps<
				HTMLAttributes<HTMLElement>,
				HTMLElement
			>;
		}
	}
}

// Eagerly load Stripe script as soon as this module is imported
// This allows preloading without exposing implementation details to consumers
const loadStripeScript = (() => {
	let scriptLoaded = false;
	return () => {
		if (!scriptLoaded) {
			const existingScript = document.querySelector(
				'script[src="https://js.stripe.com/v3/pricing-table.js"]'
			);
			if (!existingScript) {
				const script = document.createElement("script");
				script.async = true;
				script.src = "https://js.stripe.com/v3/pricing-table.js";
				document.head.appendChild(script);
				scriptLoaded = true;
			}
		}
	};
})();

// Load the script immediately when this module is imported
loadStripeScript();

// Created using https://docs.stripe.com/payments/checkout/pricing-table
interface StripePricingTableProps {
	theme?: "light" | "dark";
}

const StripePricingTable: FC<StripePricingTableProps> = ({
	theme = "light"
}) => {
	// Assume this is being used within an AuthProvider
	const { user } = useContext(AuthContext) as AuthContextType;
	const { data: userClaims } = useQuery({
		queryKey: ["user", user?.uid, "claims"],
		queryFn: async (): Promise<ParsedToken> => {
			if (user?.uid) {
				const { claims } = await user.getIdTokenResult(true);
				return claims;
			}
			return {};
		}
	});

	const prefilledEmail = userClaims?.stripe_customer_email ?? user?.email;

	// Script is loaded eagerly at module import time, no need for useEffect

	const pricingTableKey =
		theme === "light"
			? process.env.REACT_APP_STRIPE_PRICING_TABLE_ID
			: process.env.REACT_APP_STRIPE_PRICING_TABLE_DARK_MODE_ID;

	console.log(
		`Theme:[${theme}] ID:[${pricingTableKey}] Email:[${prefilledEmail}]`
	);
	return (
		<stripe-pricing-table
			pricing-table-id={pricingTableKey}
			publishable-key={process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY}
			client-reference-id={user?.uid}
			customer-email={prefilledEmail}
		></stripe-pricing-table>
	);
};

export default StripePricingTable;
